import React from 'react';

/**
 * Confirms an Edinshall portal sign-out and explains the boundary to
 * Microsoft 365.
 *
 * Phase 1 #13 scope: portal logout signs the user out of the
 * Edinshall portal session only — it does NOT propagate to the user's
 * Microsoft 365 sign-in. Auditors / IT admins need to understand this
 * so they don't assume "sign out of Edinshall" closed their wider M365
 * session, and so they know to use the M365 account menu separately if
 * they want to sign out of Microsoft entirely.
 *
 * The dialog is intentionally light — confirm/cancel only, with a plain
 * explanation of the boundary. No fancy chrome; it's a safety pause
 * before navigation, not a settings UI.
 */
export interface SignOutDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const backdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '16px'
};

const dialogStyle: React.CSSProperties = {
  background: 'var(--surface, #ffffff)',
  color: 'var(--text, #1f2933)',
  borderRadius: '8px',
  boxShadow: '0 16px 48px rgba(0, 0, 0, 0.24)',
  maxWidth: '480px',
  width: '100%',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px'
};

const footerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
  marginTop: '8px'
};

export const SignOutDialog = ({ open, onCancel, onConfirm }: SignOutDialogProps): JSX.Element | null => {
  // Trap a focusable on mount so screen readers land on the dialog.
  const confirmRef = React.useRef<HTMLButtonElement | null>(null);
  React.useEffect(() => {
    if (open && confirmRef.current) {
      confirmRef.current.focus();
    }
  }, [open]);

  // Cancel on Escape; confirm stays explicit so the user doesn't
  // accidentally sign out by pressing Enter on a focused control.
  React.useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handler = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };
    window.addEventListener('keydown', handler);
    return () => { window.removeEventListener('keydown', handler); };
  }, [open, onCancel]);

  if (!open) {
    return null;
  }

  return (
    <div
      style={backdropStyle}
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onCancel();
        }
      }}
    >
      <div
        style={dialogStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sign-out-dialog-title"
        aria-describedby="sign-out-dialog-body"
      >
        <h3 id="sign-out-dialog-title" style={{ margin: 0 }}>Sign out of the Edinshall portal?</h3>
        <div id="sign-out-dialog-body" style={{ lineHeight: 1.55 }}>
          <p style={{ marginTop: 0 }}>
            Signing out clears your Edinshall portal session on this device. You'll need to sign in again the next time you visit.
          </p>
          <p style={{ margin: 0 }}>
            <strong>Your Microsoft 365 session is separate.</strong> You'll remain signed into Microsoft on this device — open the Microsoft 365 account menu (top-right in any M365 app) to sign out of Microsoft entirely.
          </p>
        </div>
        <div style={footerStyle}>
          <button type="button" className="btn secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn" onClick={onConfirm} ref={confirmRef}>
            Sign out of Edinshall
          </button>
        </div>
      </div>
    </div>
  );
};
